import React, { useMemo } from 'react';
import {
  FormControl, InputLabel, MenuItem, Select, Skeleton,
} from '@mui/material';
import dayjs from 'dayjs';
import { FrequencyOptions } from '../../landing-page-types';
import { SyncFrequencyOptionsParams } from '../../landing-page-params';
import { frequencyHourFormatted } from '../../../../api/landing-page-api/LandingPageAPI';

export const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
// generates list of 1-28 since those are the only days present in all months
export const daysOfMonth = Array.from(Array(28), (_, index) => index + 1);
// generates list of hours 0-23 for the daily sync hour selector
export const hoursOfDay = Array.from(Array(24), (_, index) => index);

// formats an hour (0-23) into a readable label e.g. 2 -> "2am", 14 -> "2pm"
export const formatHourLabel = (hour: number) => dayjs().hour(hour).minute(0).format('ha');

export const SyncFrequencyOptions = function ({
  selectedFrequency,
  selectedDayOfMonth,
  selectedDayOfWeek,
  selectedHour,
  updateSelectedFrequency,
  updateSelectedDayOfWeek,
  updateSelectedDayOfMonth,
  updateSelectedHour,
  loadingSheetData,
}: SyncFrequencyOptionsParams) {
  if (loadingSheetData) return <Skeleton variant="rectangular" className="w-full h-12 mt-10" />;

  const isWeekly = selectedFrequency === FrequencyOptions.WEEKLY;
  const isMonthly = selectedFrequency === FrequencyOptions.MONTHLY;
  const isDaily = selectedFrequency === FrequencyOptions.DAILY;
  // weekly/monthly syncs run at the fixed default hour - daily syncs let the user pick the hour, so no static text needed
  const displayHourText = isWeekly || isMonthly;

  const frequencyText = `Syncs will occur around ${frequencyHourFormatted} PST.`;

  const frequencyOptions = useMemo(() => Object.values(FrequencyOptions), []);

  return (
    <>
      <div className="flex flex-row items-end mt-10">
        {/* Primary frequency selector */}
        <FormControl className="w-44 mr-4">
          <InputLabel>Sync Frequency</InputLabel>
          <Select
            value={selectedFrequency}
            label="Sync Frequency"
            variant="standard"
            onChange={({ target }) => {
              updateSelectedFrequency(target.value as FrequencyOptions);
            }}
          >
            {frequencyOptions.map(
              (option) => (<MenuItem key={option} value={option}>{option}</MenuItem>),
            ) }
          </Select>
        </FormControl>
        {/* Show day of week selector for weekly frequency */}
        {isWeekly && (
          <>
            on
            <FormControl className="w-44 mx-4">
              <InputLabel>Day of Week</InputLabel>
              <Select
                value={selectedDayOfWeek}
                renderValue={() => daysOfWeek[selectedDayOfWeek]}
                label="Day of Week"
                variant="standard"
                onChange={({ target }) => {
                  // use unary operator to convert target.value to number since it will always be string
                  updateSelectedDayOfWeek(+target.value);
                }}
              >
                {daysOfWeek.map((option, index) => (
                  <MenuItem key={option} value={index}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
        {/* Show day of month selector for monthly frequency */}
        {isMonthly && (
          <>
            on
            <FormControl className="w-44 mx-4">
              <InputLabel>Day of Month</InputLabel>
              <Select
                value={selectedDayOfMonth}
                label="Day of Month"
                variant="standard"
                onChange={({ target }) => {
                  // use unary operator to convert target.value to number since it will always be string
                  updateSelectedDayOfMonth(+target.value);
                }}
              >
                {daysOfMonth.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
        {/* Show hour of day selector for daily frequency */}
        {isDaily && (
          <>
            at
            <FormControl className="w-44 mx-4">
              <InputLabel>Hour</InputLabel>
              <Select
                value={selectedHour}
                renderValue={() => formatHourLabel(selectedHour)}
                label="Hour"
                variant="standard"
                onChange={({ target }) => {
                  // use unary operator to convert target.value to number since it will always be string
                  updateSelectedHour(+target.value);
                }}
              >
                {hoursOfDay.map((hour) => (
                  <MenuItem key={hour} value={hour}>{formatHourLabel(hour)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            PST
          </>
        )}
      </div>
      {/* Display text detailing the default hour of sync for weekly/monthly syncs */}
      {displayHourText && (
      <div className="text-xs mt-2">{frequencyText}</div>
      )}
    </>
  );
};
